import { getReportSignedUrl } from '@/api/reports';
import { getReportFileQuery } from '@/dashboards/Reports/helpers/getReportFileQuery';
import { Report } from '@/dashboards/Reports/types';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { downloadFile } from '@analytical-alley/common';
import { ReactNode, useCallback, useState } from 'react';
import { createContext } from 'use-context-selector';

export const ReportsContext = createContext<{
    downloadReport: (report: Report) => void;
    previewReport: (report: Report) => void;
    flushPreviewReportUrl: () => void;
    previewReportUrl: string | null;
}>({
    downloadReport: () => {},
    previewReport: () => {},
    flushPreviewReportUrl: () => {},
    previewReportUrl: null,
});

export const ReportsProvider = ({ children }: { children: ReactNode }) => {
    const {
        dashboard: { id: dashboardId },
        project: { id: projectId },
    } = useDashboardGlobalContext();
    const [previewReportUrl, setPreviewReportUrl] = useState<string | null>(
        null,
    );

    const getFileBlob = useCallback(
        async (file: Report) => {
            return getReportFileQuery({
                projectId,
                dashboardId,
                reportId: file.id,
            });
        },
        [projectId, dashboardId],
    );

    const downloadReport = useCallback(
        async (file: Report) => {
            const fileBlob = await getFileBlob(file);

            downloadFile(fileBlob, file.fileName);
        },
        [getFileBlob],
    );

    const previewReport = useCallback(
        async (file: Report) => {
            getReportSignedUrl({
                projectId,
                dashboardId,
                reportId: file.id,
            })
                .then((response) => response.signedUrl)
                .then((fileSignedUrl) => setPreviewReportUrl(fileSignedUrl));
        },
        [projectId, dashboardId],
    );

    const flushPreviewReportUrl = useCallback(
        () => setPreviewReportUrl(null),
        [],
    );

    return (
        <ReportsContext.Provider
            value={{
                downloadReport,
                previewReport,
                previewReportUrl,
                flushPreviewReportUrl,
            }}
        >
            {children}
        </ReportsContext.Provider>
    );
};
